import React, { useState } from 'react';
import { useFormik, type FormikHelpers } from 'formik';
import PasswordValidator from 'password-validator';
import { Link, useSearchParams } from 'react-router-dom';
import { object, string, ref } from 'yup';
import { InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { PAGE_TEXT } from '../../Signup/components/lang';
import { useAuth } from '../../../contexts';
import {
  TextField,
  Button,
  Alert,
  type AlertDetails,
  IconButton,
} from '../../../components';
import { SuccessPage } from '../../../components/SuccessPage/SuccessPage';

const pwdSchema = new PasswordValidator();

pwdSchema
  .is()
  .min(8)
  .is()
  .max(20)
  .has()
  .letters() // Must have letters
  .has()
  .digits() // Must have digits
  .has()
  .symbols(); // Must have symbols

const validationSchema = object({
  email: string().email(PAGE_TEXT.VALID_EMAIL).required(PAGE_TEXT.REQUIRED),
  password: string()
    .required(PAGE_TEXT.REQUIRED)
    .test(
      'is-strong-password',
      PAGE_TEXT.VALID_PASSWORD,
      value => pwdSchema.validate(value) === true,
    ),
  confirmPassword: string()
    .oneOf([ref('password')], PAGE_TEXT.PASSWORD_MATCH)
    .required(PAGE_TEXT.REQUIRED),
});

type ResetPasswordForm = {
  email: string;
  password: string;
  confirmPassword: string;
};

export const ResetPassword: React.FunctionComponent = () => {
  const initialValues = {
    email: '',
    password: '',
    confirmPassword: '',
  };
  const { resetPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const [showPassword, setShowPassword] = useState(false);

  const [submissionStatus, setSubmissionStatus] = useState<boolean>(false);

  const handleSubmit = async (
    values: ResetPasswordForm,
    { setSubmitting }: FormikHelpers<ResetPasswordForm>,
  ): Promise<void> => {
    const { email, password } = values;
    const token = searchParams.get('token') ?? '';
    try {
      const { data } = await resetPassword(email, password, token);
      if (data) {
        setSubmissionStatus(true);
      }
      setOpenAlert(true);
      setAlertDetails({
        alertContent: `Password reset successfully. Please log in with your new password.`,
        alertType: 'success',
      });
      window.location.replace('/login');
    } catch (error) {
      setOpenAlert(true);
      setAlertDetails({
        alertContent:
          `Password reset failed, please try again later.` +
          (error as Error).message,
        alertType: 'error',
      });
    }
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  return (
    <div>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />

      {submissionStatus ? (
        <div
          style={{
            padding: '4rem',
          }}
        >
          <SuccessPage
            icon="succeed"
            title="Password Reset Successfully"
            message="You have successfully reset your password. Please log in with your new password."
            action={<Link to="/login">{PAGE_TEXT.BACK_TO_LOGIN}</Link>}
          />
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '2rem',
          }}
        >
          <h1>{PAGE_TEXT.RESET_PASSWORD}</h1>
          <form
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
            onSubmit={formik.handleSubmit}
          >
            <div style={{ padding: '5px', width: '400px' }}>
              <TextField
                className="w-full"
                label={PAGE_TEXT.EMAIL}
                required
                id="login-email"
                name="email"
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                InputLabelProps={{ shrink: true }}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </div>
            <div style={{ padding: '5px', width: '400px' }}>
              <TextField
                className="w-full"
                required
                label={PAGE_TEXT.PASSWORD}
                id="login-password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                sx={{
                  '.MuiInputBase-root': {
                    backgroundColor: '#ffffff',
                  },
                }}
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                InputLabelProps={{ shrink: true }}
                helperText={PAGE_TEXT.VALID_PASSWORD}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        color="primary"
                        size="small"
                        aria-label={
                          showPassword
                            ? 'hide the password'
                            : 'display the password'
                        }
                        onClick={() => {
                          setShowPassword(!showPassword);
                        }}
                        edge="end"
                        icon={showPassword ? <VisibilityOff /> : <Visibility />}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <div style={{ padding: '5px', width: '400px' }}>
              <TextField
                className="w-full"
                label={PAGE_TEXT.CONFIRM_PASSWORD}
                id="signup-confirm-password"
                name="confirmPassword"
                type="password"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.confirmPassword &&
                  Boolean(formik.errors.confirmPassword)
                }
                helperText={
                  formik.touched.confirmPassword &&
                  formik.errors.confirmPassword
                }
              />
            </div>
            <div style={{ padding: '0.5rem' }}>
              <Button
                style={{ margin: '0.5rem' }}
                type="submit"
                variant="contained"
                disabled={formik.isSubmitting}
              >
                {PAGE_TEXT.RESET_PASSWORD}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
